import { useCallback } from 'react';
import { usePlatform } from '@/platform';
import { useUiStore } from '@/shared/hooks/use-shallow-store';
import type { SoundCue } from './types';

export type PlaySound = (cue: SoundCue) => void;

/**
 * Returns a stable `play(cue)` bound to the current `AudioSlice` state.
 * Reads `volume` + `muted` reactively so a slider/mute change takes effect
 * without re-mounting the consumer. The underlying `AudioPlayer` is
 * resolved through `PlatformContext` so each platform can swap its impl
 * without touching feature code.
 */
export const useSound = (): PlaySound => {
  const volume = useUiStore((s) => s.volume);
  const muted = useUiStore((s) => s.muted);
  const audio = usePlatform().audio;
  return useCallback(
    (cue) => {
      if (muted) return;
      audio.play(cue, { volume });
    },
    [volume, muted, audio],
  );
};
